import { createContext, useContext, useState, useEffect, useCallback } from "react";

const CartContext = createContext(null);
const KEY = "gg_cart";

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(KEY)) || [];
    } catch {
      return [];
    }
  });
  const [open, setOpen] = useState(false);
  const [lastCollection, setLastCollection] = useState(null);

  useEffect(() => {
    localStorage.setItem(KEY, JSON.stringify(items));
  }, [items]);

  const addItem = useCallback((product, size) => {
    const isLive = (product.status || "coming_soon") === "live";
    const allowBackorder = !!product.allow_backorder;
    const maxStock = Number((product.size_inventory || {})[size] ?? 0);
    const cap = !isLive || allowBackorder ? Infinity : Math.max(0, maxStock);
    setItems((prev) => {
      const id = `${product.slug}__${size}`;
      const existing = prev.find((i) => i.id === id);
      if (existing) {
        return prev.map((i) => (i.id === id ? { ...i, qty: Math.min(cap || 1, i.qty + 1) } : i));
      }
      return [
        ...prev,
        {
          id,
          slug: product.slug,
          name: product.name,
          price: product.price,
          size,
          image: product.images?.[0],
          status: product.status || "coming_soon",
          collection: product.collection,
          allowBackorder,
          maxStock,
          qty: 1,
        },
      ];
    });
    setLastCollection(product.collection || null);
    setOpen(true);
  }, []);

  const removeItem = useCallback((id) => {
    setItems((prev) => prev.filter((i) => i.id !== id));
  }, []);

  const updateQty = useCallback((id, delta) => {
    setItems((prev) =>
      prev
        .map((i) => {
          if (i.id !== id) return i;
          const cap = i.status !== "live" || i.allowBackorder ? Infinity : Math.max(1, i.maxStock ?? Infinity);
          return { ...i, qty: Math.min(cap, Math.max(1, i.qty + delta)) };
        })
        .filter((i) => i.qty > 0)
    );
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const count = items.reduce((n, i) => n + i.qty, 0);
  const subtotal = items.reduce((n, i) => n + i.qty * (i.price || 0), 0);

  const liveItems = items.filter((i) => i.status === "live");
  const liveCount = liveItems.reduce((n, i) => n + i.qty, 0);
  const liveSubtotal = liveItems.reduce((n, i) => n + i.qty * (i.price || 0), 0);
  const removeLiveItems = useCallback(() => {
    setItems((prev) => prev.filter((i) => i.status !== "live"));
  }, []);

  return (
    <CartContext.Provider
      value={{
        items, addItem, removeItem, updateQty, clear, count, subtotal, open, setOpen,
        liveItems, liveCount, liveSubtotal, removeLiveItems, lastCollection,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
}
